import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Globe, Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useContent } from "../context/ContentContext";
import { IMAGES } from "../mock";

const Navbar = () => {
  const { lang, changeLang, t } = useLanguage();
  const { content } = useContent();
  const logoSrc = content.logo_image || IMAGES.logo;
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav_home") },
    { to: "/about", label: t("nav_about") },
    { to: "/services", label: t("nav_services") },
    { to: "/gallery", label: t("nav_gallery") },
    { to: "/booking", label: t("nav_booking") },
  ];

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <div className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
      <nav
        className={`bf-glass bf-navbar-shadow max-w-7xl mx-auto rounded-2xl px-5 md:px-6 transition-all duration-300 ${
          scrolled ? "py-2" : "py-3"
        }`}
      >
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={logoSrc}
              alt="Barbo Factory"
              className="w-11 h-11 object-contain drop-shadow-[0_0_12px_rgba(34,211,238,0.4)] group-hover:scale-105 transition-transform"
            />
            <span className="text-white font-extrabold tracking-wide text-lg group-hover:text-[#67e8f9] transition-colors">
              BARBO FACTORY
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive(l.to)
                    ? "text-[#0a1024] bg-gradient-to-r from-[#67e8f9] to-[#22d3ee] shadow-[0_0_20px_-4px_rgba(34,211,238,0.6)]"
                    : "text-gray-300 hover:text-white hover:bg-white/5"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 border border-white/10">
              <Globe className="w-4 h-4 text-gray-400" />
              <button
                onClick={() => changeLang("pt")}
                className={`text-xs font-semibold transition-colors ${lang === "pt" ? "text-[#67e8f9]" : "text-gray-500 hover:text-gray-300"}`}
              >
                PT
              </button>
              <span className="text-gray-600 text-xs">|</span>
              <button
                onClick={() => changeLang("en")}
                className={`text-xs font-semibold transition-colors ${lang === "en" ? "text-[#67e8f9]" : "text-gray-500 hover:text-gray-300"}`}
              >
                EN
              </button>
            </div>
            <button
              className="lg:hidden text-white p-1"
              onClick={() => setOpen((o) => !o)}
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden mt-3 pt-3 border-t border-white/10 flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium ${
                  isActive(l.to) ? "text-[#0a1024] bg-gradient-to-r from-[#67e8f9] to-[#22d3ee]" : "text-gray-300 hover:bg-white/5"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <div className="flex items-center gap-2 px-4 py-2">
              <Globe className="w-4 h-4 text-gray-400" />
              <button onClick={() => changeLang("pt")} className={`text-xs font-semibold ${lang === "pt" ? "text-[#67e8f9]" : "text-gray-500"}`}>PT</button>
              <span className="text-gray-600 text-xs">|</span>
              <button onClick={() => changeLang("en")} className={`text-xs font-semibold ${lang === "en" ? "text-[#67e8f9]" : "text-gray-500"}`}>EN</button>
            </div>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Navbar;
